import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { formatRelativeTimeShort } from '@client/new/helpers/time';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useCommentsModel } from './comments.model';
import { Comment } from './comments.domain';
import { useKeepScrollPosition } from '@client/new/hooks/useKeepScrollPosition';

const getDisplayName = (comment: Comment): string => {
  if (comment.author.userName) {
    return comment.author.userName;
  }

  return `User ${comment.author.userId}`;
};

const handleCommentActionError = (error: unknown, action: string): void => {
  toast.error(`Failed to ${action.toLowerCase()}`);
};

const createOptimisticComment = (
  contentId: string,
  text: string,
  threadId?: string,
): Comment => {
  const isReply = !!threadId;
  const tempId = isReply
    ? `temp-reply-${Date.now()}-${Math.random()}`
    : `temp-${Date.now()}-${Math.random()}`;
  const now = new Date().toISOString();

  return {
    id: tempId,
    contentId,
    threadId: threadId || null,
    text,
    author: {
      userId: 0,
      userName: 'Creating...',
      userAvatarURL: '',
    },
    hidden: false,
    date: now,
    thread: null,
  };
};

const addOptimisticComment = (
  comments: Comment[],
  optimisticComment: Comment,
  threadId?: string,
): Comment[] => {
  if (threadId) {
    return comments.map((comment) => {
      if (comment.id === threadId) {
        return {
          ...comment,
          thread: [...(comment.thread || []), optimisticComment],
        };
      }
      return comment;
    });
  }
  return [optimisticComment, ...comments];
};

const removeOptimisticComment = (
  comments: Comment[],
  tempId: string,
  threadId?: string,
): Comment[] => {
  if (threadId) {
    return comments.map((comment) => {
      if (comment.id === threadId) {
        return {
          ...comment,
          thread: comment.thread?.filter((c) => c.id !== tempId) || null,
        };
      }
      return comment;
    });
  }
  return comments.filter((c) => c.id !== tempId);
};

const updateCommentVisibility = (
  comments: Comment[],
  commentId: string,
  hidden: boolean,
): Comment[] => {
  return comments.map((comment) => {
    if (comment.id === commentId) {
      return { ...comment, hidden };
    }
    if (comment.thread) {
      const updatedThread = comment.thread.map((threadComment) =>
        threadComment.id === commentId
          ? { ...threadComment, hidden }
          : threadComment,
      );
      return { ...comment, thread: updatedThread };
    }
    return comment;
  });
};

const findCommentInfo = (
  comments: Comment[],
  commentId: string,
): { comment: Comment | undefined; isReply: boolean } => {
  let foundComment: Comment | undefined = undefined;
  let isReply = false;

  for (const c of comments) {
    if (c.id === commentId) {
      foundComment = c;
    }
    if (c.thread && c.thread.some((t) => t.id === commentId)) {
      isReply = true;
    }
    if (foundComment && isReply) break;
  }

  return { comment: foundComment, isReply };
};

const isParentCommentWithThread = (comment: Comment | undefined): boolean => {
  return !!comment?.thread && comment.thread.length > 0;
};

const deleteThreadReply = (
  comments: Comment[],
  commentId: string,
): Comment[] => {
  return comments.map((comment) => {
    if (comment.thread) {
      const updatedThread = comment.thread.filter((t) => t.id !== commentId);
      return {
        ...comment,
        thread: updatedThread.length > 0 ? updatedThread : null,
      };
    }
    return comment;
  });
};

const deleteParentComment = (
  comments: Comment[],
  commentId: string,
): Comment[] => {
  return comments.filter((c) => c.id !== commentId);
};

const deleteCommentFromState = (
  comments: Comment[],
  commentId: string,
): { updatedComments: Comment[]; deletedComment: Comment | null } => {
  const { comment, isReply } = findCommentInfo(comments, commentId);

  if (isReply) {
    return {
      updatedComments: deleteThreadReply(comments, commentId),
      deletedComment: null,
    };
  }

  return {
    updatedComments: deleteParentComment(comments, commentId),
    deletedComment: comment || null,
  };
};

type NavigationState = {
  viewMode: CommentsViewMode;
  navigationStack: CommentsViewMode[];
  openThreadId: string | null;
};

const navigateTo = (
  currentState: NavigationState,
  newMode: CommentsViewMode,
  threadId?: string,
): NavigationState => {
  const isThreadMode = newMode === 'thread' || newMode === 'thread-hidden';

  return {
    viewMode: newMode,
    navigationStack: [...currentState.navigationStack, currentState.viewMode],
    openThreadId: isThreadMode ? threadId || currentState.openThreadId : null,
  };
};

const navigateBack = (state: NavigationState): NavigationState => {
  if (state.navigationStack.length === 0) {
    return state;
  }

  const previousMode = state.navigationStack[state.navigationStack.length - 1];
  const isThreadMode =
    previousMode === 'thread' || previousMode === 'thread-hidden';

  return {
    viewMode: previousMode,
    navigationStack: state.navigationStack.slice(0, -1),
    openThreadId: isThreadMode ? state.openThreadId : null,
  };
};

export type ViewComment = Comment & {
  displayName: string;
  formattedTime: string;
  shouldShowReplyButton: boolean;
};

export type CommentsViewMode =
  | 'comments'
  | 'hidden'
  | 'thread'
  | 'thread-hidden';

export type CommentsVMActionMap = {
  ToggleVisibility: { commentId: string; isVisible: boolean };
  RefreshComments: null;
  NavigateToHidden: null;
  NavigateToThread: { commentId: string };
  NavigateToThreadHidden: null;
  NavigateToComments: null;
  NavigateBack: null;
  CreateComment: { text: string };
  CreateThreadReply: { threadId: string; text: string };
  DeleteComment: { commentId: string };
};

export type CommentsVMAction = Actions<CommentsVMActionMap>;

export type CommentsViewModel = {
  name: 'CommentsViewModel';
  displayedComments: ViewComment[];
  contentId: string;
  hiddenCount: number;
  visibleCount: number;
  threadHiddenCount: number;
  threadVisibleCount: number;
  viewMode: CommentsViewMode;
  navigationStack: CommentsViewMode[];
  canGoBack: boolean;
  openThreadId: string;
  openThreadComment: ViewComment | null;
  openThreadReplies: ViewComment[];
  isCreatingComment: boolean;
  isCreatingReply: boolean;
  shouldShowHiddenChip: boolean;
  shouldShowThreadHiddenChip: boolean;
  shouldShowReplyButton: boolean;
} & Dispatchers<CommentsVMActionMap>;

export type CommentsVMParams = {
  contentId: string | null;
};

const toViewComment = (
  comment: Comment,
  shouldShowReplyButton: boolean,
): ViewComment => ({
  ...comment,
  displayName: getDisplayName(comment),
  formattedTime: formatRelativeTimeShort(new Date(comment.date)),
  shouldShowReplyButton: shouldShowReplyButton && !comment.hidden,
});

export const useCommentsViewModel = (
  p: CommentsVMParams,
): Async<CommentsViewModel> => {
  const model = useCommentsModel({ contentId: p.contentId });
  const [localComments, setLocalComments] = useState<Comment[]>([]);
  const [viewMode, setViewMode] = useState<CommentsViewMode>('comments');
  const [navigationStack, setNavigationStack] = useState<CommentsViewMode[]>(
    [],
  );
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [isCreatingReply, setIsCreatingReply] = useState(false);

  useKeepScrollPosition(p.contentId || '', '');

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const intercom = (window as any).Intercom;
    if (intercom) intercom('update', { hide_default_launcher: true });
    return () => {
      if (intercom) intercom('update', { hide_default_launcher: false });
    };
  }, []);

  const modelComments =
    model.data.type === 'fetched' ? model.data.value.comments : undefined;

  useEffect(() => {
    if (modelComments) {
      setLocalComments(modelComments);
    }
  }, [modelComments]);

  const openThreadComment = openThreadId
    ? localComments.find((c) => c.id === openThreadId) || null
    : null;
  const openThreadReplies = openThreadComment?.thread || [];

  const handleCommentCreation = useCallback(
    async (text: string, threadId?: string) => {
      if (!p.contentId) return;

      const isReply = !!threadId;
      const optimisticComment = createOptimisticComment(
        p.contentId,
        text,
        threadId,
      );

      setLocalComments((prev) =>
        addOptimisticComment(prev, optimisticComment, threadId),
      );

      if (isReply) {
        setIsCreatingReply(true);
      } else {
        setIsCreatingComment(true);
      }

      try {
        await model.createComment({
          contentId: p.contentId,
          text,
          threadId,
        });

        toast.success(isReply ? 'Reply created' : 'Comment created');
        setLocalComments((prev) =>
          removeOptimisticComment(prev, optimisticComment.id, threadId),
        );
      } catch (error) {
        setLocalComments((prev) =>
          removeOptimisticComment(prev, optimisticComment.id, threadId),
        );
        handleCommentActionError(
          error,
          isReply ? 'create reply' : 'create comment',
        );
      } finally {
        if (isReply) {
          setIsCreatingReply(false);
        } else {
          setIsCreatingComment(false);
        }
      }
    },
    [p.contentId, model],
  );

  const _dispatch = useCallback(
    async (action: CommentsVMAction) => {
      switch (action.type) {
        case 'ToggleVisibility': {
          const { commentId, isVisible } = action.payload;

          setLocalComments((prev) =>
            updateCommentVisibility(prev, commentId, !isVisible),
          );

          try {
            await model.toggleVisibility(commentId, isVisible);
          } catch (error) {
            setLocalComments((prev) =>
              updateCommentVisibility(prev, commentId, isVisible),
            );
            handleCommentActionError(error, 'update comment visibility');
          }
          return;
        }

        case 'RefreshComments': {
          try {
            await model.refetch();
          } catch (error) {
            handleCommentActionError(error, 'refresh comments');
          }
          return;
        }

        case 'NavigateToHidden': {
          const navState = navigateTo(
            { viewMode, navigationStack, openThreadId },
            'hidden',
          );
          setViewMode(navState.viewMode);
          setNavigationStack(navState.navigationStack);
          setOpenThreadId(navState.openThreadId);
          return;
        }

        case 'NavigateToThread': {
          const { commentId } = action.payload;
          const navState = navigateTo(
            { viewMode, navigationStack, openThreadId },
            'thread',
            commentId,
          );
          setViewMode(navState.viewMode);
          setNavigationStack(navState.navigationStack);
          setOpenThreadId(navState.openThreadId);
          return;
        }

        case 'NavigateToThreadHidden': {
          const navState = navigateTo(
            { viewMode, navigationStack, openThreadId },
            'thread-hidden',
          );
          setViewMode(navState.viewMode);
          setNavigationStack(navState.navigationStack);
          setOpenThreadId(navState.openThreadId);
          return;
        }

        case 'NavigateToComments': {
          const navState = navigateTo(
            { viewMode, navigationStack, openThreadId },
            'comments',
          );
          setViewMode(navState.viewMode);
          setNavigationStack(navState.navigationStack);
          setOpenThreadId(navState.openThreadId);
          return;
        }

        case 'NavigateBack': {
          const navState = navigateBack({
            viewMode,
            navigationStack,
            openThreadId,
          });
          setViewMode(navState.viewMode);
          setNavigationStack(navState.navigationStack);
          setOpenThreadId(navState.openThreadId);
          return;
        }

        case 'CreateComment': {
          const { text } = action.payload;
          await handleCommentCreation(text);
          return;
        }

        case 'CreateThreadReply': {
          const { threadId, text } = action.payload;
          await handleCommentCreation(text, threadId);
          return;
        }

        case 'DeleteComment': {
          const { commentId } = action.payload;

          const originalComments = localComments;
          const { updatedComments, deletedComment } = deleteCommentFromState(
            localComments,
            commentId,
          );

          setLocalComments(updatedComments);

          try {
            await model.deleteComment(commentId);
            const successMessage = isParentCommentWithThread(deletedComment)
              ? 'Comment and thread deleted'
              : 'Comment deleted';
            toast.success(successMessage);
          } catch (error) {
            setLocalComments(originalComments);
            handleCommentActionError(error, 'delete comment');
          }
          return;
        }
      }
    },
    [
      model,
      viewMode,
      navigationStack,
      openThreadId,
      localComments,
      handleCommentCreation,
    ],
  );

  const dispatchers = getDispatchers<CommentsVMActionMap>(_dispatch);

  const shouldShowReplyButtonForComments =
    viewMode === 'comments' || viewMode === 'hidden';
  const shouldShowReplyButtonForThread =
    viewMode === 'thread' || viewMode === 'thread-hidden';

  const allComments = localComments.map((c) =>
    toViewComment(c, shouldShowReplyButtonForComments),
  );
  const visibleComments = allComments.filter((c) => !c.hidden);
  const hiddenComments = allComments.filter((c) => c.hidden);

  const displayedComments = useMemo(() => {
    const visibleThreadReplies = openThreadReplies.filter((c) => !c.hidden);
    const hiddenThreadReplies = openThreadReplies.filter((c) => c.hidden);

    switch (viewMode) {
      case 'hidden':
        return hiddenComments;
      case 'thread':
        return visibleThreadReplies.map((c) =>
          toViewComment(c, shouldShowReplyButtonForThread),
        );
      case 'thread-hidden':
        return hiddenThreadReplies.map((c) =>
          toViewComment(c, shouldShowReplyButtonForThread),
        );
      case 'comments':
      default:
        return visibleComments;
    }
  }, [
    viewMode,
    hiddenComments,
    openThreadReplies,
    visibleComments,
    shouldShowReplyButtonForThread,
  ]);

  const threadHiddenCount = useMemo(
    () => openThreadReplies.filter((c) => c.hidden).length,
    [openThreadReplies],
  );
  const threadVisibleCount = useMemo(
    () => openThreadReplies.filter((c) => !c.hidden).length,
    [openThreadReplies],
  );

  const hiddenCount = hiddenComments.length;
  const visibleCount = visibleComments.length;

  const shouldShowHiddenChip = viewMode === 'comments' && hiddenCount > 0;
  const shouldShowThreadHiddenChip =
    viewMode === 'thread' && threadHiddenCount > 0;
  const shouldShowReplyButton =
    viewMode === 'comments' || viewMode === 'thread';

  if (model.data.type !== 'fetched') {
    return model.data;
  }

  return toAsyncFetched({
    name: 'CommentsViewModel' as const,
    displayedComments,
    contentId: model.data.value.contentId,
    hiddenCount,
    visibleCount,
    threadHiddenCount,
    threadVisibleCount,
    viewMode,
    navigationStack,
    canGoBack: navigationStack.length > 0,
    openThreadId: openThreadId || '',
    openThreadComment: openThreadComment
      ? toViewComment(openThreadComment, shouldShowReplyButtonForComments)
      : null,
    openThreadReplies: openThreadReplies.map((c) =>
      toViewComment(c, shouldShowReplyButtonForThread),
    ),
    isCreatingComment,
    isCreatingReply,
    shouldShowHiddenChip,
    shouldShowThreadHiddenChip,
    shouldShowReplyButton,
    ...dispatchers,
  }) as Async<CommentsViewModel>;
};
